import React, { useState, useCallback } from 'react';
import PiecesGrid from '../../components/grids/PiecesGrid';
import EditPieceForm from '../../components/FormComps/Pieces/EditPieceForm';
import {
  Drawer,
  DrawerContent,
  DrawerNavigation,
} from '@progress/kendo-react-layout';
import { FaTimesCircle } from 'react-icons/fa';
import '../Styles.css';

interface PiecesProps {
  lockEdit?: boolean;
}

const Pieces = (props: PiecesProps) => {
  const [expanded, setExpanded] = useState(false);
  const [editFormVisible, setEditFormVisible] = useState(false);
  const [selectedPieceId, setSelectedPieceId] = useState<number | null>(null);
  const [piecesList, setPiecesList] = useState<number[]>([]);
  const [frozenPiecesList, setFrozenPiecesList] = useState<number[]>([]); // Frozen list for navigation
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleExpanded = useCallback(() => {
    setExpanded((prevState) => !prevState);
    if (editFormVisible) {
      setEditFormVisible(false);
      // Clear frozen list when closing the drawer
      setFrozenPiecesList([]);
    }
  }, [editFormVisible]);

  const handleSelect = useCallback(() => {
    setExpanded(false);
    setEditFormVisible(false);
  }, []);

  const handleEditForm = useCallback((pieceId: number) => {
    setSelectedPieceId(pieceId);
    setEditFormVisible(true);
    setExpanded(true);
    // Freeze the current pieces list for consistent navigation
    setFrozenPiecesList(piecesList);
  }, [piecesList]);

  const handlePieceUpdated = useCallback(() => {
    // Refresh grid data by incrementing the trigger
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const handleNavigateToPiece = useCallback((pieceId: number) => {
    setSelectedPieceId(pieceId);
  }, []);

  const getCurrentPieceIndex = useCallback(() => {
    if (selectedPieceId && frozenPiecesList.length > 0) {
      return frozenPiecesList.indexOf(selectedPieceId);
    }
    return undefined;
  }, [selectedPieceId, frozenPiecesList]);

  const handlePiecesListUpdate = useCallback((pieces: number[]) => {
    setPiecesList(pieces);
  }, []);

  return (
    <div>
      <Drawer
        expanded={expanded}
        position={'end'}
        width={1600} // Much wider to accommodate PDF viewer + form comfortably
        mode='overlay'
        animation={{
          duration: 400,
        }}
        onOverlayClick={handleExpanded}
        onSelect={handleSelect}
      >
        <DrawerContent>
          <DrawerNavigation>
            {editFormVisible && selectedPieceId && (
              <>
                <div className='form__title__container'>
                  <h1 className='form__title'>Edit Piece</h1>
                  <FaTimesCircle
                    className='closeform'
                    onClick={handleExpanded}
                  />
                </div>
                <EditPieceForm
                  lockEdit={props.lockEdit || false}
                  onPieceId={selectedPieceId}
                  onPieceUpdated={handlePieceUpdated}
                  onClose={handleExpanded}
                  onNavigateToPiece={handleNavigateToPiece}
                  piecesList={frozenPiecesList}
                  currentPieceIndex={getCurrentPieceIndex()}
                />
              </>
            )}
          </DrawerNavigation>

          <PiecesGrid
            lockEdit={props.lockEdit || false}
            onEditToggle={handleEditForm}
            onPiecesListUpdate={handlePiecesListUpdate}
            refreshTrigger={refreshTrigger}
          />
        </DrawerContent>
      </Drawer>
    </div>
  );
};

export default Pieces;